/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import * as animation from '@constant/animation';
import ModalBackground from '@component/common/modal/modal-background';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import otterLogoImage from '@asset/image/otter-brick-logo.webp';
import ImageBlock from '@component/common/block/image-block';
import IconBlock from '@component/common/block/icon-block';

const Modal = styled.div<{ height: string }>`
  display: flex;
  position: relative;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  background-color: ${color.WHITE_1};
  padding: ${size.MARGIN_2};
  border-radius: ${size.BORDER_RADIUS_2};
  min-width: 200px;
  min-height: 150px;
  height: ${(props) => props.height};
  margin: 0px auto;

  ${animation.MODAL_FADE_IN_CSS}
`;

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  margin-bottom: ${size.MARGIN_1};
  width: 100%;
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: ${size.MARGIN_1};
  right: ${size.MARGIN_1};
  cursor: pointer;
`;

const LOGO_DESIGN = {
  box: {
    width: '100%',
    margin: '0px',
    alignX: 'center',
  },
  image: { height: '60px' },
} as const;

interface IProps {
  children: React.ReactNode;
  height?: string;
  hideLogo?: boolean;
  hideCloseButton?: boolean;
  closeOnBackgroundClick?: boolean;
  onClose?: () => void;
}

const ModalTemplate: React.FC<IProps> = ({
  children,
  height = 'auto',
  hideLogo = false,
  hideCloseButton = false,
  closeOnBackgroundClick = true,
  onClose,
}) => {
  const dispatch = useTypedDispatch();

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    dispatch(modalActions.hideModal());
  };

  return (
    <ModalBackground
      onClick={() => {
        if (closeOnBackgroundClick) handleClose();
      }}>
      <Modal height={height} onClick={(e) => e.stopPropagation()}>
        {!hideLogo && (
          <Header>
            <ImageBlock src={otterLogoImage} alt='otterbrick' design={LOGO_DESIGN} />
          </Header>
        )}
        {children}
        {!hideCloseButton && (
          <CloseButtonContainer>
            <IconBlock
              icon='close'
              design={{
                icon: { size: 'medium', kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: handleClose }}
            />
          </CloseButtonContainer>
        )}
      </Modal>
    </ModalBackground>
  );
};

export default ModalTemplate;
